import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { studentAPI } from '../utils/api';

const RevisionCard = ({ revision, onRevise }) => {
  const isOverdue = revision.status === 'overdue';
  const dayColors = {
    1: { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
    4: { bg: '#FEF3C7', color: '#B45309', border: '#FDE68A' },
    7: { bg: '#E6F4EC', color: '#1B7F3E', border: '#BBF7D0' },
  };
  const dc = isOverdue
    ? { bg: '#FEE2E2', color: '#DC2626', border: '#FECACA' }
    : dayColors[revision.revision_number] || dayColors[1];

  return (
    <div style={{
      background: 'white', border: `1.5px solid ${isOverdue ? '#FECACA' : 'var(--border)'}`,
      borderRadius: 'var(--radius)', padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: '12px',
      cursor: 'pointer', transition: 'box-shadow 0.15s',
    }}
      onClick={() => onRevise(revision.topic_id)}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: dc.bg, border: `1.5px solid ${dc.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <div style={{ fontSize: '9px', fontWeight: 800, color: dc.color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{isOverdue ? '⚠' : 'DAY'}</div>
        <div style={{ fontSize: '18px', fontWeight: 900, color: dc.color, lineHeight: 1 }}>{isOverdue ? 'DUE' : revision.revision_number}</div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text)', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {revision.topic_name}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
          {revision.subject} · {revision.chapter_name}
        </div>
        <div style={{ fontSize: '10px', fontWeight: 700, color: isOverdue ? '#DC2626' : 'var(--muted)' }}>
          {isOverdue ? `⚠ Overdue since ${revision.due_date}` : `Due: ${revision.due_date}`}
        </div>
      </div>

      {revision.quiz_score !== null && revision.quiz_score !== undefined && (
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: 900, color: revision.quiz_score >= 70 ? '#1B7F3E' : '#DC2626' }}>{revision.quiz_score}%</div>
          <div style={{ fontSize: '9px', color: 'var(--muted)', fontWeight: 600 }}>last score</div>
        </div>
      )}

      <button
        onClick={e => { e.stopPropagation(); onRevise(revision.topic_id); }}
        style={{ background: isOverdue ? '#DC2626' : 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Revise Now →
      </button>
    </div>
  );
};

const RevisionsPage = () => {
  const [revisions, setRevisions] = useState([]);
  const [allRevisions, setAllRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const navigate = useNavigate();

  useEffect(() => { loadRevisions(); }, []);

  const loadRevisions = async () => {
    try {
      const res = await studentAPI.getTodayRevisions();
      setRevisions(res.data.data || []);
    } catch {
      setRevisions([]);
    }

    try {
      const allRes = await studentAPI.getAllRevisions();
      setAllRevisions(allRes.data.data || []);
    } catch {
      setAllRevisions([]);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const overdueCount = revisions.filter(r => r.status === 'overdue').length;
  const todayCount = revisions.filter(r => r.status !== 'overdue').length;
  const upcomingRevisions = allRevisions.filter(r => r.due_date > today);

  const tabRevisions = tab === 'today' ? revisions
    : tab === 'upcoming' ? upcomingRevisions
    : allRevisions;

  return (
    <AppShell>
      <div className="topbar">
        <div style={{ fontSize: '15px', fontWeight: 800 }}>📅 My Revisions</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {overdueCount > 0 && (
            <span style={{ background: '#FEE2E2', color: '#DC2626', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '20px' }}>
              ⚠ {overdueCount} Overdue
            </span>
          )}
          {todayCount > 0 && (
            <span style={{ background: 'var(--brand-primary-light)', color: 'var(--brand-primary)', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '20px' }}>
              📅 {todayCount} Due Today
            </span>
          )}
        </div>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* How 1-4-7 works */}
        <div className="card fade-up-1" style={{ background: 'var(--brand-secondary)', padding: '16px 20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 800, color: 'white', marginBottom: '10px' }}>⚡ How the 1-4-7 System Works</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            {[
              { day: 'Day 1', color: '#BFDBFE', text: 'Immediate reinforcement while memory is fresh' },
              { day: 'Day 4', color: '#FDE68A', text: 'Mid-term consolidation to prevent forgetting' },
              { day: 'Day 7', color: '#BBF7D0', text: 'Long-term encoding to permanent memory' },
            ].map(r => (
              <div key={r.day} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 900, color: r.color, marginBottom: '4px' }}>{r.day}</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{r.text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { id: 'today', label: `Due Today (${revisions.length})` },
            { id: 'upcoming', label: `Upcoming (${upcomingRevisions.length})` },
            { id: 'all', label: `All Pending (${allRevisions.length})` },
          ].map(item => (
            <button key={item.id} onClick={() => setTab(item.id)}
              style={{ padding: '7px 14px', borderRadius: '20px', border: `1.5px solid ${tab === item.id ? 'var(--brand-primary)' : 'var(--border)'}`, background: tab === item.id ? 'var(--brand-primary-light)' : 'white', color: tab === item.id ? 'var(--brand-primary)' : 'var(--muted)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
              {item.label}
            </button>
          ))}
        </div>

        {/* Revision list */}
        <div className="fade-up-2" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
          ) : tabRevisions.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
              <div style={{ fontSize: '16px', fontWeight: 800, marginBottom: '8px' }}>
                {tab === 'today' ? 'All caught up!' : 'No revisions here'}
              </div>
              <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '20px' }}>
                {tab === 'today' ? 'No revisions due today. Keep studying new topics!' : 'Study more topics to schedule revisions.'}
              </p>
              <button className="btn-primary" onClick={() => navigate('/subjects')}>Study New Topics</button>
            </div>
          ) : (
            tabRevisions.map(r => (
              <RevisionCard key={r.id} revision={r} onRevise={(topicId) => navigate(`/study/${topicId}`)} />
            ))
          )}
        </div>

      </div>
    </AppShell>
  );
};

export default RevisionsPage;
